import { createContext, useContext } from 'react';

import type {
  ControlAppearance,
  ControlMaxWidth,
  ControlMinWidth,
  ControlSize,
  ControlVariantInput,
  ControlWidth,
} from './types';
import { normalizeControlVariant } from './normalizeControlVariant';

export type ControlContextValue = {
  appearance?: ControlAppearance;
  ghost?: boolean;
  size?: ControlSize;
  variant?: ControlVariantInput;
  width?: ControlWidth;
  minWidth?: ControlMinWidth;
  maxWidth?: ControlMaxWidth;
};

const ControlContext = createContext<ControlContextValue | undefined>(undefined);

/**
 * Provides default appearance, size, and/or variant for descendent controls (e.g. Button, Select).
 * Pass only the keys you need; children will fall back to their own defaults for omitted keys.
 *
 * @example
 *   <ControlProvider value={{ size: 'small', variant: 'neutral' }}>
 *     <Button>Small secondary button</Button>
 *     <Select placeholder="Choose…" />
 *   </ControlProvider>
 */
export const ControlProvider = ControlContext.Provider;

/**
 * Returns the current control appearance from context if defined, otherwise returns the default value.
 */
export const useControlAppearance = (
  controlledValue: ControlAppearance | undefined,
  defaultValue: ControlAppearance = 'vivid',
): ControlAppearance => {
  const contextValue = useContext(ControlContext);
  return controlledValue ?? contextValue?.appearance ?? defaultValue;
};

/**
 * Returns the current control ghost flag from context if defined, otherwise returns the default value.
 */
export const useControlGhost = (
  controlledValue: boolean | undefined,
  defaultValue = false,
): boolean => {
  const contextValue = useContext(ControlContext);
  return controlledValue ?? contextValue?.ghost ?? defaultValue;
};

/**
 * Returns the current control size from context if defined, otherwise returns the default value.
 */
export const useControlSize = (
  controlledValue?: ControlSize,
  defaultValue: ControlSize = 'medium',
): ControlSize => {
  const contextValue = useContext(ControlContext);
  return controlledValue ?? contextValue?.size ?? defaultValue;
};

/**
 * Returns the current control variant from context if defined, otherwise returns the default value.
 */
export const useControlVariant = (
  controlledValue?: ControlVariantInput,
  defaultValue: ControlVariantInput = 'brand',
) => {
  const contextValue = useContext(ControlContext);
  return normalizeControlVariant(controlledValue ?? contextValue?.variant ?? defaultValue);
};

/**
 * Returns the current control width from context if defined, otherwise returns undefined.
 */
export const useControlWidth = (controlledValue?: ControlWidth): ControlWidth | undefined => {
  const contextValue = useContext(ControlContext);
  return controlledValue ?? contextValue?.width;
};

/**
 * Returns the current control min width from context if defined, otherwise returns undefined.
 */
export const useControlMinWidth = (
  controlledValue?: ControlMinWidth,
): ControlMinWidth | undefined => {
  const contextValue = useContext(ControlContext);
  return controlledValue ?? contextValue?.minWidth;
};

/**
 * Returns the current control max width from context if defined, otherwise returns undefined.
 */
export const useControlMaxWidth = (
  controlledValue?: ControlMaxWidth,
): ControlMaxWidth | undefined => {
  const contextValue = useContext(ControlContext);
  return controlledValue ?? contextValue?.maxWidth;
};
